/**
 * Create some control elements for history listing rows (edit, delete, etc)
 */
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { isNil } from 'Utility/ramdaReplacement';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';

import ActionButton from '../ActionButton';
import { Container, Item } from '../Grid';
import ToolTip from '../ToolTip';

import { smallActionButtonDimensions } from '../../config/theme/componentTheme';
import DocumentCreationButton from '../DocumentCreationButton';
import { useAppSelector as useSelector } from 'Store/index';
import CircularProgressLocal from '../CircularProgress';
import { getJWTData } from '../../utility/jwtAuthTools';
import { canStartEditing, hasWritePermission } from '../../containers/ReadWritePermissions';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import Icon from 'Components/_NewElements/Icon';
import { EditHistoryMenuItem } from './EditHistory/EditHistoryDialog';
import { blurActiveElement } from 'Utility/domFocus';

const DeleteDialog = React.lazy(() => import('./DeleteDialog'));
const ConfirmationDialog = React.lazy(() => import('Components/ConfirmationDialog'));

const StyledSection = styled('div', { shouldForwardProp: prop => prop !== 'hideButton' && prop !== 'minWidth' })(
  ({ hideButton, minWidth }: { hideButton?: boolean; minWidth?: string }) => ({
    width: '100%',
    minWidth: hideButton ? undefined : minWidth ? minWidth : '21.7rem',
  }),
);

const StyledMenuIcon = styled(
  ({ onClick, noPadding }: { onClick?: (e: React.MouseEvent<Element>) => void; noPadding?: boolean }) => (
    <IconButton sx={{ padding: noPadding ? 0 : undefined }} onClick={onClick}>
      <MoreVertIcon />
    </IconButton>
  ),
)`
  color: #6c96ae;
  cursor: pointer;
`;

const MenuItemWithIcon = ({
  text,
  onClick,
  icon,
  disabled,
}: {
  text: string | React.JSX.Element;
  onClick?: (e: React.MouseEvent<Element>) => void;
  icon?: React.JSX.Element;
  disabled?: boolean;
}) => {
  return (
    <MenuItem disabled={disabled}>
      <Container alignItems={'center'} onClick={onClick}>
        {icon && <Item style={{ padding: '0 1rem 0 0', width: '3rem' }}>{icon}</Item>}
        <Item xs={true}>{text}</Item>
      </Container>
    </MenuItem>
  );
};

const attachTooltip = (element: React.JSX.Element, tip?: React.JSX.Element): React.JSX.Element => (
  <ToolTip title={tip} content={element} hover={true} />
);

const LoadingComponent = ({ size = '1.5rem' }: { size?: string }) => (
  <Container alignItems="center" justifyContent="center">
    <Item>
      <CircularProgressLocal size={size} />
    </Item>
  </Container>
);

const HistoryRowControls = ({
  document,
  startEdit,
  view,
  subDocs,
  itemAlign,
  buttonDimensions,
  hideButton,
  showEditInMenu,
  deleteContent,
  deleteHandler,
  newStyle = false,
  editConfirmationText,
  deleteConfirmationText,
  disableButtons,
  enableButtons,
  minWidth,
  hideEditHistory,
  noMenuIconPadding,
}: IHistoryRowControls): React.JSX.Element => {
  // Where the menu will spawn
  const [anchor, setAnchor] = React.useState<null | Element | Element>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState<false | 'edit' | 'delete'>(false);

  const { allowEditingLockedDocument } = React.useContext(EditingLockedContentContext);

  const toggleMenu = (e: React.MouseEvent<Element>): void => {
    e.stopPropagation();
    e.preventDefault();
    if (anchor) blurActiveElement();
    setAnchor(!anchor ? e.currentTarget : null);
  };

  const toggleDeleteDialog = (e: React.MouseEvent<Element>): void => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDelete = (e: React.MouseEvent<Element>): void => {
    e.stopPropagation();
    e.preventDefault();
    toggleDeleteDialog(e);
    toggleMenu(e);
  };

  const buttonDimensionsAdjusted = { ...smallActionButtonDimensions, ...buttonDimensions };

  const orgId = getJWTData()?.orgid;

  // User doesn't have write permission to this document if the document is from another org and there is no grants that allow writing
  const noWritePermission = document._ownerOrg !== orgId && !hasWritePermission(document);
  // If no write permissions, check also if the document is lockedFor the other organization
  const lockedForOtherOrg = !isNil((document as IControlProps)._lockedFor) && !allowEditingLockedDocument(document);

  const cannotEditThisDoc = !canStartEditing(document);

  const deletionDisabled = !enableButtons && document._ownerOrg !== orgId;
  const editingDisabled = !enableButtons && (disableButtons || noWritePermission || lockedForOtherOrg);

  const medicationEndReasonEnabled =
    editingDisabled &&
    (document as IControlProps)._type === 'medication' &&
    (document as IMedication).hasEnded?.[0] === true;

  const isCreating = useSelector((s: IState) => s.documents.isCreating);

  const loading = document._id ? isCreating?.id === document._id : false;

  const openConfirmation = (type: 'delete' | 'edit', _toggleMenu?: true) => (e: React.MouseEvent<Element>) => {
    _toggleMenu && toggleMenu(e);
    setConfirmationDialogOpen(type);
  };

  const ActionButtonComponent = newStyle ? ActionButtonRounded : ActionButton;

  return (
    <StyledSection hideButton={hideButton} minWidth={minWidth}>
      <Container justifyContent="flex-end" alignItems={itemAlign ?? 'center'}>
        <Item>
          {!hideButton && (
            <React.Fragment>
              {!view ? (
                <DocumentCreationButton
                  name={document._type || ''}
                  document={document}
                  text="general.edit"
                  onClick={
                    !loading ? (editConfirmationText ? openConfirmation('edit') : startEdit(document)) : undefined
                  }
                  width={buttonDimensionsAdjusted.width}
                  height={buttonDimensionsAdjusted.height}
                  fontSize={buttonDimensionsAdjusted.fontSize}
                  disabled={editingDisabled}
                  enabled={enableButtons}
                  newStyle={newStyle}
                  disabledTooltip={
                    lockedForOtherOrg ? (
                      <FormattedMessage id="general.documentLockedIntegration" />
                    ) : (
                      <FormattedMessage id="general.documentLocked" />
                    )
                  }
                  uppercase={!newStyle}
                />
              ) : (
                <ActionButtonComponent
                  text="general.view"
                  onClick={view.startView(document)}
                  width={buttonDimensionsAdjusted.width}
                  height={buttonDimensionsAdjusted.height}
                  fontSize={buttonDimensionsAdjusted.fontSize}
                  disabled={disableButtons}
                />
              )}
            </React.Fragment>
          )}
        </Item>
        <Item style={{ width: '4.5rem' }}>
          <Container justifyContent="flex-end" alignItems={itemAlign ?? 'center'}>
            <Item>
              {!disableButtons && ((view && !editingDisabled) || !deletionDisabled || medicationEndReasonEnabled) ? (
                <StyledMenuIcon noPadding={noMenuIconPadding} onClick={toggleMenu} />
              ) : (
                attachTooltip(
                  <div>
                    <StyledMenuIcon noPadding={noMenuIconPadding} />
                  </div>,
                  disableButtons ? (
                    <FormattedMessage id="general.locked" />
                  ) : lockedForOtherOrg ? (
                    <FormattedMessage id="general.documentLockedIntegration" />
                  ) : (
                    <FormattedMessage id="general.documentLocked" />
                  ),
                )
              )}
              <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={toggleMenu}>
                {(view || showEditInMenu) && !(editingDisabled || cannotEditThisDoc) && (
                  <MenuItemWithIcon
                    text={loading ? <LoadingComponent /> : <FormattedMessage id="general.edit" />}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleMenu(e);
                      return !loading
                        ? editConfirmationText
                          ? openConfirmation('edit', true)(e)
                          : startEdit(document)(e)
                        : undefined;
                    }}
                    icon={<Icon icon="edit" size={1.8} />}
                  />
                )}

                {!hideEditHistory && <EditHistoryMenuItem document={document} />}

                {!deletionDisabled && (
                  <MenuItemWithIcon
                    text={<FormattedMessage id="general.deletionTitle" />}
                    onClick={deleteConfirmationText ? openConfirmation('delete', true) : handleDelete}
                    icon={<Icon icon="close" color={'errorDefault'} size={2} />}
                  />
                )}
              </Menu>
            </Item>
          </Container>
        </Item>
      </Container>

      <React.Suspense
        fallback={
          <div style={{ position: 'absolute', right: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <CircularProgressLocal size={'3rem'} />
          </div>
        }
      >
        {confirmationDialogOpen && (
          <ConfirmationDialog
            open={!!confirmationDialogOpen}
            text={
              confirmationDialogOpen === 'delete' && deleteConfirmationText
                ? deleteConfirmationText
                : confirmationDialogOpen === 'edit' && editConfirmationText
                  ? editConfirmationText
                  : ''
            }
            cancel={{
              callback: () => {
                setConfirmationDialogOpen(false);
              },
            }}
            confirm={{
              callback: e => {
                if (confirmationDialogOpen === 'delete') {
                  setConfirmationDialogOpen(false);
                  toggleDeleteDialog(e);
                } else if (confirmationDialogOpen === 'edit') {
                  startEdit(document)({} as React.MouseEvent);
                }
              },
              text: 'general.continue',
              loading: loading,
            }}
          />
        )}
        {deleteDialogOpen && (
          <DeleteDialog
            document={document as IControlProps}
            open={deleteDialogOpen}
            setOpen={toggleDeleteDialog}
            subDocs={subDocs}
            customDeleteContent={deleteContent}
            customDeleteHandler={deleteHandler}
          />
        )}
      </React.Suspense>
    </StyledSection>
  );
};

export interface IHistoryRowControls {
  document: IControlProps;
  startEdit: (document: any) => (e: React.MouseEvent) => void;
  view?: IView;
  subDocs?: Array<{ name: string; id: string } | undefined>;
  itemAlign?: string;
  buttonDimensions?: {
    width?: number;
    height?: number;
    fontSize?: number;
  };
  hideButton?: boolean;
  showEditInMenu?: boolean;
  /** Custom delete dialog content */
  deleteContent?: React.JSX.Element;
  /** Custom delete handler to substitute default document deletion handler */
  deleteHandler?: () => void;
  newStyle?: boolean;
  editConfirmationText?: React.JSX.Element | string;
  deleteConfirmationText?: React.JSX.Element | string;
  disableButtons?: boolean;
  /** Override write permissions etc. that might disable editing/viewing. Does not enable deleting docs from another org. */
  enableButtons?: boolean;
  /** minWidth for the whole component, rems */
  minWidth?: string;
  hideEditHistory?: boolean;
  /** To disable iconbutton padding from MenuIcon */
  noMenuIconPadding?: boolean;
}

export default HistoryRowControls;
